import { put, select } from 'redux-saga/effects'

import {
  type DashboardActions,
  InternalProjectActions as Internal,
} from '../../actions'
import { selectDashboard } from '../../selectors'
import type { ProjectFeature, ProjectItem, Workspace } from '../../types'

// The backend computes features from the user's plan/role, so every own
// (non-shared) project in a workspace has the same set — copy from a
// neighbour. The fallback for an empty workspace assumes the owner set; the
// dashboard refetch on mount replaces it with the server-computed one.
const resolveFeatures = (workspace: Workspace): ProjectFeature[] => {
  const sibling = workspace.folders
    .flatMap((folder) => folder.projects)
    .find((project) => !project.sharedBy)

  if (sibling) {
    return sibling.features
  }

  const archiveFeature: ProjectFeature =
    workspace.planType === 'plan_free' ? 'archive_badge' : 'archive'

  return ['delete', 'move', 'transfer', 'duplicate', archiveFeature]
}

export function* created({
  payload: { id, teamId, title, theme },
}: ReturnType<typeof DashboardActions.projects.created>) {
  const workspaces: Workspace[] = yield select(selectDashboard)

  const workspace = workspaces.find((w) =>
    teamId ? w.id === teamId : w.type === 'personal'
  )
  const folder = workspace?.folders.find((f) => f.type === 'dashboard')

  if (!workspace || !folder) {
    return
  }

  const now = new Date().toISOString()

  const project: ProjectItem = {
    id,
    title: title || 'Untitled',
    theme,
    createdAt: now,
    updatedAt: now,
    thumbnail: null,
    teamId,
    sharedBy: null,
    archived: false,
    folderId: null,
    published: false,
    features: resolveFeatures(workspace),
    // Only the editor's save path reaches this saga, and the editor never
    // saves a report.
    type: 'sitemap',
  }

  yield put(
    Internal.add({ project, folderId: folder.id, workspaceId: workspace.id })
  )
}
